import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import { Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import api from '../utils/api';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const XLSX = require('xlsx');

interface TrainingUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TrainingPreview {
  title: string;
  description: string;
  training_type: string;
  is_required: boolean;
  duration_minutes: number;
  sections_count: number;
  content_blocks_count: number;
  quiz_questions_count: number;
}

interface JsonTrainingPreview {
  title: string;
  sections_count: number;
  content_blocks_count: number;
}

function summarizeJsonTraining(t: any): JsonTrainingPreview {
  const sections = t?.sections;
  const n = Array.isArray(sections) ? sections.length : 0;
  let blocks = 0;
  if (Array.isArray(sections)) {
    sections.forEach((s: any) => {
      blocks += Array.isArray(s?.content_blocks) ? s.content_blocks.length : 0;
    });
  }
  return {
    title: (t?.title && String(t.title)) || 'Untitled',
    sections_count: n,
    content_blocks_count: blocks
  };
}

const TrainingUploadDialog: React.FC<TrainingUploadDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [uploadTab, setUploadTab] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [jsonPayload, setJsonPayload] = useState<any>(null);
  const [jsonFileName, setJsonFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<TrainingPreview[]>([]);
  const [jsonPreview, setJsonPreview] = useState<JsonTrainingPreview[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [uploadResult, setUploadResult] = useState<any>(null);

  useEffect(() => {
    if (!open) return;
    setUploadTab(0);
    setFile(null);
    setJsonPayload(null);
    setJsonFileName(null);
    setPreview([]);
    setJsonPreview([]);
    setErrors([]);
    setWarnings([]);
    setUploadResult(null);
  }, [open]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setErrors([]);
      setWarnings([]);
      setPreview([]);
      setUploadResult(null);
      
      // Validate file type
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrors(['Please select an Excel file (.xlsx or .xls)']);
        return;
      }
      
      // Parse the Excel file to show real preview
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
                     const workbook = XLSX.read(data, { type: 'array' });
          
          // Check if required sheets exist
          const requiredSheets = ['Training Metadata', 'Sections', 'Content Blocks', 'Quiz Questions'];
          const missingSheets = requiredSheets.filter(sheet => !workbook.SheetNames.includes(sheet));
          
          console.log('Available sheets:', workbook.SheetNames);
          console.log('Required sheets:', requiredSheets);
          console.log('Missing sheets:', missingSheets);
          
          if (missingSheets.length > 0) {
            setErrors([`Missing required sheets: ${missingSheets.join(', ')}`]);
            return;
          }
          
          // Parse Training Metadata sheet
          const trainingSheet = workbook.Sheets['Training Metadata'];
                     const trainingData = XLSX.utils.sheet_to_json(trainingSheet);
          
          console.log('Training data from sheet:', trainingData);
          
          if (trainingData.length === 0) {
            setErrors(['Training Metadata sheet is empty']);
            return;
          }
          
          // Create preview from actual data
          const previewData = trainingData.map((row: any) => {
            // Count sections for this training
            const sectionsSheet = workbook.Sheets['Sections'];
                         const sectionsData = XLSX.utils.sheet_to_json(sectionsSheet);
            const sectionsCount = sectionsData.filter((s: any) => s.training_title === row.title).length;
            
            // Count content blocks for this training
            const contentBlocksSheet = workbook.Sheets['Content Blocks'];
                         const contentBlocksData = XLSX.utils.sheet_to_json(contentBlocksSheet);
            const contentBlocksCount = contentBlocksData.filter((cb: any) => cb.training_title === row.title).length;
            
            // Count quiz questions for this training
            const quizSheet = workbook.Sheets['Quiz Questions'];
                         const quizData = XLSX.utils.sheet_to_json(quizSheet);
            const quizCount = quizData.filter((q: any) => q.training_title === row.title).length;
            
            console.log('Processing row:', row);
            console.log('Sections count:', sectionsCount);
            console.log('Content blocks count:', contentBlocksCount);
            console.log('Quiz count:', quizCount);
            
            return {
              title: row.title || 'Untitled',
              description: row.description || 'No description',
              training_type: row.training_type || 'compliance',
              is_required: row.is_required || false,
              duration_minutes: row.duration_minutes || 0,
              sections_count: sectionsCount,
              content_blocks_count: contentBlocksCount,
              quiz_questions_count: quizCount
            };
          });
          
          console.log('Final preview data:', previewData);
          setPreview(previewData);
          
        } catch (error) {
          console.error('Excel parsing error:', error);
          setErrors(['Failed to parse Excel file. Please check the file format.']);
        }
      };
      
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleJsonFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setJsonFileName(selectedFile.name);
    setJsonPayload(null);
    setJsonPreview([]);
    setErrors([]);
    setWarnings([]);
    setUploadResult(null);

    if (!selectedFile.name.toLowerCase().endsWith('.json')) {
      setErrors(['Please select a JSON file (.json)']);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        const obj = JSON.parse(text);
        if (obj.schema_version != null && obj.schema_version !== 1) {
          setErrors([`Unsupported schema_version: ${obj.schema_version} (only 1 is supported)`]);
          return;
        }
        let trainings: any[] = [];
        if (Array.isArray(obj.trainings)) {
          trainings = obj.trainings;
        } else if (obj.training && typeof obj.training === 'object') {
          trainings = [obj.training];
        } else {
          setErrors(['JSON must contain "training" or "trainings"']);
          return;
        }
        if (trainings.length === 0) {
          setErrors(['No training objects found in file']);
          return;
        }
        setJsonPayload(obj);
        setJsonPreview(trainings.map(summarizeJsonTraining));
      } catch (e) {
        console.error(e);
        setErrors(['Invalid JSON file']);
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleExcelUpload = async () => {
    if (!file) return;

    setUploading(true);
    setErrors([]);
    setWarnings([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/trainings/bulk_upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      
      if (response.data.success) {
        setWarnings(response.data.warnings || []);
        onSuccess();
      } else {
        setErrors(response.data.errors || ['Upload failed']);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setErrors([
        error.response?.data?.error || 
        error.response?.data?.detail || 
        'Upload failed. Please check your file and try again.'
      ]);
    } finally {
      setUploading(false);
    }
  };

  const handleJsonUpload = async () => {
    if (!jsonPayload) return;

    setUploading(true);
    setErrors([]);
    setWarnings([]);

    try {
      const response = await api.post('/trainings/import_json/', jsonPayload);
      setUploadResult(response.data);
      if (response.data.success) {
        setWarnings(response.data.warnings || []);
        onSuccess();
      } else {
        const apiErrors = response.data.errors;
        if (Array.isArray(apiErrors) && apiErrors.length) {
          setErrors(apiErrors.map((e: any) => (typeof e === 'string' ? e : JSON.stringify(e))));
        } else {
          setErrors([response.data.error || 'Import failed']);
        }
      }
    } catch (error: any) {
      console.error('JSON import error:', error);
      const d = error.response?.data;
      setErrors([
        d?.error ||
          d?.detail ||
          (Array.isArray(d?.errors) ? d.errors.map((e: any) => JSON.stringify(e)).join('; ') : null) ||
          'Import failed. Please check your file and try again.',
      ]);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setJsonPayload(null);
    setJsonFileName(null);
    setPreview([]);
    setJsonPreview([]);
    setErrors([]);
    setWarnings([]);
    setUploadResult(null);
    setUploadTab(0);
    onClose();
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/trainings/download_template/', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'training_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const downloadJsonTemplate = async () => {
    try {
      const response = await api.get('/trainings/download_json_template/', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/json' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'training_template.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('JSON template download error:', error);
      alert('Failed to download JSON template. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="h6">Upload trainings</Typography>
          {uploadTab === 0 && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              variant="outlined"
              size="small"
            >
              Download Excel template
            </Button>
          )}
          {uploadTab === 1 && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={downloadJsonTemplate}
              variant="outlined"
              size="small"
            >
              Download JSON template
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={uploadTab}
          onChange={(_, v) => {
            setUploadTab(v);
            setErrors([]);
            setWarnings([]);
          }}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Excel" />
          <Tab label="JSON" />
        </Tabs>

        {!uploadResult && uploadTab === 0 && (
          <>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload an Excel file with multiple trainings. The file should have four sheets:
              &quot;Training Metadata&quot;, &quot;Sections&quot;, &quot;Content Blocks&quot;, and &quot;Quiz Questions&quot;.
            </Typography>

            <Box mb={3}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="training-file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="training-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={uploading}
                >
                  Select Excel File
                </Button>
              </label>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </Box>

            {errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.map((error, index) => (
                  <Typography key={index} variant="body2">
                    {error}
                  </Typography>
                ))}
              </Alert>
            )}

            {preview.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Preview ({preview.length} trainings)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Sections</TableCell>
                        <TableCell>Content Blocks</TableCell>
                        <TableCell>Quiz Questions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.map((training, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {training.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {training.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={training.training_type} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={training.is_required ? 'Yes' : 'No'} 
                              size="small" 
                              color={training.is_required ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{training.duration_minutes} min</TableCell>
                          <TableCell>{training.sections_count}</TableCell>
                          <TableCell>{training.content_blocks_count}</TableCell>
                          <TableCell>{training.quiz_questions_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}

        {!uploadResult && uploadTab === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" paragraph>
              Import a JSON file with <code>schema_version: 1</code> and either a <code>training</code> object or a{' '}
              <code>trainings</code> array (same shape as API export). Suitable for editing in an external tool or AI
              assistant.
            </Typography>

            <Box mb={3}>
              <input
                accept=".json,application/json"
                style={{ display: 'none' }}
                id="training-json-upload"
                type="file"
                onChange={handleJsonFileSelect}
              />
              <label htmlFor="training-json-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={uploading}
                >
                  Select JSON File
                </Button>
              </label>
              {jsonFileName && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {jsonFileName}
                </Typography>
              )}
            </Box>

            {errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.map((error, index) => (
                  <Typography key={index} variant="body2">
                    {error}
                  </Typography>
                ))}
              </Alert>
            )}

            {jsonPreview.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Preview ({jsonPreview.length} trainings)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Sections</TableCell>
                        <TableCell>Content blocks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jsonPreview.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.title}</TableCell>
                          <TableCell>{row.sections_count}</TableCell>
                          <TableCell>{row.content_blocks_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}

        {uploadResult && (
          <Box>
            <Alert 
              severity={uploadResult.success ? 'success' : 'error'} 
              sx={{ mb: 2 }}
            >
              {uploadResult.success ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Upload Successful!
                  </Typography>
                  <Typography variant="body2">
                    {uploadResult.message ||
                      (uploadResult.success
                        ? `Imported ${uploadResult.created_count ?? (uploadResult.created_titles?.length || 0)} training(s).`
                        : '')}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2">
                  Upload failed. Please check the errors below.
                </Typography>
              )}
            </Alert>

            {uploadResult.success && (
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  Processed Trainings
                </Typography>
                <ul>
                  {(uploadResult.processed_trainings || uploadResult.created_titles || []).map(
                    (title: string, index: number) => (
                    <li key={index}>
                      <Typography variant="body2">{title}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}

            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Warnings
                </Typography>
                <ul>
                  {warnings.map((warning, index) => (
                    <li key={index}>
                      <Typography variant="body2">{warning}</Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {errors.length > 0 && (
              <Alert severity="error">
                <Typography variant="h6" gutterBottom>
                  Errors
                </Typography>
                <ul>
                  {errors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!uploadResult ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={uploadTab === 0 ? handleExcelUpload : handleJsonUpload}
              variant="contained"
              disabled={
                uploading || (uploadTab === 0 ? !file : !jsonPayload)
              }
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            >
              {uploading ? 'Uploading...' : uploadTab === 0 ? 'Upload Excel' : 'Import JSON'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TrainingUploadDialog;
